import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AccessControlService } from 'src/@services/access-control.service';
import { CommonService } from 'src/@services/common.service';
import { environment } from 'src/environments/environment';
import {
  LOCAL_STORAGE_PORT,
  SESSION_STORAGE_PORT,
  StoragePort,
} from './providers';

interface RoutePermission {
  permission: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private _router: Router,
    private _cS: CommonService,
    private activatedRoute: ActivatedRoute,
    private accessControlService: AccessControlService,
    @Inject(LOCAL_STORAGE_PORT) private localStoragePort: StoragePort,
    @Inject(SESSION_STORAGE_PORT) private sessionStoragePort: StoragePort
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const token = this._cS.getTokenObj();
    const isSiteSelected =
      this.localStoragePort.getItem('IsSiteSelected') == 'true' ? true : false;
    const IsSiteProgramPeriodVisible = JSON.parse(
      this.sessionStoragePort.getItem('SiteDetails') || '{}'
    ).IsProgramPeriodVisible;
    const IsSiteTagVisible = JSON.parse(
      this.sessionStoragePort.getItem('SiteDetails') || '{}'
    ).IsTagVisible;
    document.title =
      (route.data['title'] ? route.data['title'] : 'Login') +
      ' - PayBev' +
      (environment.apiURL.includes('staging') ? ' Staging' : '');
    const uuid = route.paramMap.get('siteId');

    if (token != null) {
      let currentUrl = this._router.url;
      this._router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          currentUrl = event.url;
        }
      });
      if (!uuid) {
        this._router.navigate(['/switch-account']);
        return false;
      }
      if (
        (IsSiteProgramPeriodVisible != null &&
          IsSiteProgramPeriodVisible == false) ||
        (IsSiteTagVisible != null && IsSiteTagVisible == false)
      ) {
        this._router.events.subscribe((event: any) => {
          if (event instanceof NavigationEnd) {
            currentUrl = event.url;
            return true;
          }
        });
      } else if (!isSiteSelected) {
        this.localStoragePort.setItem('IsSiteSelected', 'true');
      }

      return this.checkModulePermission(state.url, route);
    }

    if (window.location.href.includes('?t=')) {
      return true;
    }
    this._router.navigate(['/login']);
    return false;
  }

  private checkModulePermission(
    url: string,
    route: ActivatedRouteSnapshot
  ): boolean | UrlTree {
    const siteId = this.findSiteId(route);
    if (this.isUrlPermitted(url, siteId)) {
      return true;
    }

    return this._router.parseUrl(this.getLandingUrl(`/paybev/${siteId}`));
  }

  /**
   * Whether this role may open the URL. Public because the SSO hand-off has to ask the same
   * question a second time: on the way in the guard waves a ?t= URL through, since the role's
   * permissions do not exist until SsoLogin has answered.
   */
  isUrlPermitted(url: string, siteId: string | null): boolean {
    const path = url.split('?')[0];
    if (
      path.includes('/my-profile') ||
      path.includes('/help-and-support')
    ) {
      return true;
    }

    if (!siteId || !path.startsWith(`/paybev/${siteId}`)) {
      return true;
    }

    const routes = this.getProtectedRoutes(`/paybev/${siteId}`);
    const matched = routes.find((entry) => path.startsWith(entry.url));
    return !matched || matched.permission;
  }

  private getProtectedRoutes(base: string): RoutePermission[] {
    const ac = this.accessControlService;
    const siteDetails = JSON.parse(
      this.sessionStoragePort.getItem('SiteDetails') || '{}'
    );
    const isProgramPeriodVisible =
      siteDetails?.IsProgramPeriodVisible != null
        ? siteDetails.IsProgramPeriodVisible
        : true;
    const isTagVisible =
      siteDetails?.IsTagVisible != null ? siteDetails.IsTagVisible : true;

    const routes: RoutePermission[] = [
      { permission: !!ac.canAccessUsers().canViewUsers, url: `${base}/users` },
      {
        permission: !!ac.canAccessInvestors().canViewInvestors,
        url: `${base}/investors`,
      },
      {
        permission: !!ac.canAccessVendors().canViewVendors,
        url: `${base}/vendors`,
      },
      {
        permission:
          !!ac.canAccessProgramsTags().canViewProgramsTags &&
          isProgramPeriodVisible,
        url: `${base}/program-periods`,
      },
      {
        permission:
          !!ac.canAccessTags().canViewTags && isTagVisible,
        url: `${base}/tags`,
      },
      {
        permission: !!ac.canAccessNotifications().canViewNotifications,
        url: `${base}/notifications`,
      },
      {
        permission: !!ac.canAccessSummary().canViewSummary,
        url: `${base}/summary`,
      },
      {
        permission: !!ac.canAccessActivity().canViewActivity,
        url: `${base}/activity-detailed`,
      },
      {
        permission: !!ac.canAccessInvestments().canViewInvestments,
        url: `${base}/investments`,
      },
      {
        permission: !!ac.canAccessPayments().canAddPayment,
        url: `${base}/payments`,
      },
      {
        permission: ac.canViewOutstandingStatement(),
        url: `${base}/report-outstanding-statement`,
      },
      {
        permission: ac.canViewActivityExport(),
        url: `${base}/report-activity-export`,
      },
      {
        permission: ac.canViewAccountStatements(),
        url: `${base}/report-account-statements`,
      },
      {
        permission: ac.canViewInternalReports(),
        url: `${base}/report-internal-reports`,
      },
      {
        permission: ac.canViewProjections(),
        url: `${base}/report-projections`,
      },
    ];

    return routes.sort((a, b) => b.url.length - a.url.length);
  }

  /**
   * First page this role can open on the site (Summary in most cases). Used for permission
   * redirects and the header logo. Falls back to User Profile, which every signed-in user can open.
   */
  getLandingUrl(base: string): string {
    const ac = this.accessControlService;
    const siteDetails = JSON.parse(
      this.sessionStoragePort.getItem('SiteDetails') || '{}'
    );
    const isProgramPeriodVisible =
      siteDetails?.IsProgramPeriodVisible != null
        ? siteDetails.IsProgramPeriodVisible
        : true;
    const isTagVisible =
      siteDetails?.IsTagVisible != null ? siteDetails.IsTagVisible : true;

    const settingsPages = [
      {
        permission: !!ac.canAccessInvestors().canViewInvestors,
        url: `${base}/investors`,
      },
      {
        permission: !!ac.canAccessVendors().canViewVendors,
        url: `${base}/vendors`,
      },
      {
        permission:
          !!ac.canAccessProgramsTags().canViewProgramsTags &&
          isProgramPeriodVisible,
        url: `${base}/program-periods`,
      },
      {
        permission:
          !!ac.canAccessTags().canViewTags && isTagVisible,
        url: `${base}/tags`,
      },
      {
        permission: !!ac.canAccessUsers().canViewUsers,
        url: `${base}/users`,
      },
      {
        permission: !!ac.canAccessNotifications().canViewNotifications,
        url: `${base}/notifications`,
      },
    ];
    const settingUrl =
      settingsPages.find((page) => page.permission)?.url ?? `${base}/investors`;

    const ordered = [
      {
        permission: !!ac.canAccessSummary().canViewSummary,
        url: `${base}/summary`,
      },
      {
        permission: !!ac.canAccessActivity().canViewActivity,
        url: `${base}/activity-detailed`,
      },
      {
        permission: !!ac.canAccessInvestments().canViewInvestments,
        url: `${base}/investments`,
      },
      {
        permission: !!ac.canAccessPayments().canAddPayment,
        url: `${base}/payments`,
      },
      {
        permission: !!ac.canAccessReports(),
        url: `${base}/${ac.getFirstReportsLandingPath()}`,
      },
      {
        permission: settingsPages.some((page) => page.permission),
        url: settingUrl,
      },
    ];

    return ordered.find((page) => page.permission)?.url ?? `${base}/user-profile/details`;
  }

  private findSiteId(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const siteId = current.paramMap.get('siteId');
      if (siteId) {
        return siteId;
      }
      current = current.parent;
    }
    return null;
  }
}
